// NOTE: YOU SHOULD NOT BE IMPORTING FROM THIS FILE DIRECTLY.
// Instead, generate the individual test modules by running this command:
// `npm run generate-ab-tests`
//
// Then, import from an individual test module - for instance, if your test is called
// 'my_teams_current_ab_test', then import like this:
//
// ```typescript
// import { abTest as myTeamsCurrentAbTest } from 'js/ab_tests/tests/my_teams_current_ab_test';
//
// console.log(`This user's bucket: ${myTeamsCurrentAbTest()}`);
// ```
//
// Or, to use the React hook version:
// ```typescript
// import React from 'react';
// import { useAbTest as useMyTeamsCurrentAbTest } from 'js/ab_tests/tests/my_teams_current_ab_test';
//
// const MyComponent = () => {
//   const bucket = useMyTeamsCurrentAbTest();
//   return <div>{`This user's bucket: ${bucket}`}</div>;
// };
// ```

// This module is responsible for bucketing users into A/B tests. It can be called with all tests,
// active, inactive, or finished.
//
// Bucketing rules:
//
// An *active* test will resolve to the variant the user belongs to, based on their user ID.
//
// An *inactive* test will resolve to the first variant (usually 'control').
//
// A *finished* test will resolve to the variant set as the winner.
//
// If the test has a *holdout* key set, then **all three** types of tests will also consider if the
// user is in the holdout group, and return 'holdout' instead of a variant if they are.

import { useEffect } from 'react';
import Cookies from 'js-cookie';
import md5 from 'md5';
import { captureMessage } from '@sentry/browser';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { keptAlivePost } from 'js/utils/kept_alive_post';
import User from 'js/core_post_webpack/models/user';
import { currentUser } from 'js/utils/user';

import {
  getTestOverrideFromCookie,
  setTestOverrideInCookie,
} from 'js/admin_user_tests';

type Alternative<A extends string> = A | { name: A; percent: number };

// These types are a bit convoluted, but they're set up this way just to ensure that
// branching logic always considers the right test configuration, if this module is
// changed. A typical test configuration will look pretty simple:
//
// const TEST_CONFIG = {
//   active: false,
//   finished: true,
//   alternatives: ['control', 'variant'],
//   holdout: 'rev_holdout_q3_2024',
//   winner: 'variant',
// } as const;

type BaseTestConfig<A extends string> = {
  active?: boolean;

  // Require at least one alternative (using a partial 'tuple')
  alternatives: readonly [Alternative<A>, ...Alternative<A>[]];
};

type ActiveTestConfig<A extends string> = BaseTestConfig<A> & {
  finished: false;
};

type FinishedTestConfig<A extends string> = BaseTestConfig<A> & {
  finished: true;
  winner: A;
  winner_holdout_percent?: number;
};

type HoldoutActiveTestConfig<A extends string> = ActiveTestConfig<A> & {
  holdout: string;
  holdout_percent?: number;
};

type HoldoutFinishedTestConfig<A extends string> = FinishedTestConfig<A> & {
  holdout: string;
  holdout_percent?: number;
};

type TestConfig<A extends string> =
  | ActiveTestConfig<A>
  | FinishedTestConfig<A>
  | HoldoutActiveTestConfig<A>
  | HoldoutFinishedTestConfig<A>;

function getCurrentUserId() {
  return User?.current?.id || currentUser?.id;
}

async function record_ab_test(test: string, variant: string): Promise<void> {
  // keptAlivePost is async and non-blocking
  await keptAlivePost(routes.api_v0_ab_tests_path({ subdomain_param: 'api' }), {
    test,
    variant,
    user_id: getCurrentUserId(),
    auvid: window.$auvid,
  });
  setTestOverrideInCookie(test, variant);
}

function isVariant<A extends string>(
  str: string | undefined,
  alternatives: TestConfig<A>['alternatives'],
): str is A {
  if (!str) return false;

  for (const alt of alternatives) {
    if (typeof alt === 'string') {
      if (str === alt) return true;
    } else {
      if (str === alt.name) return true;
    }
  }

  return false;
}

function isHoldoutTest<A extends string>(
  testConfig: TestConfig<A>,
): testConfig is HoldoutActiveTestConfig<A> | HoldoutFinishedTestConfig<A> {
  return 'holdout' in testConfig;
}

function getVariantFromFinishedTest<A extends string>(
  testConfig: FinishedTestConfig<A> | HoldoutFinishedTestConfig<A>,
) {
  if (isHoldoutTest(testConfig)) {
    const holdoutPercent = testConfig.holdout_percent || 10;
    const userId = getCurrentUserId();

    if (userId && inHoldout(testConfig.holdout, holdoutPercent, userId)) {
      return 'holdout';
    }
  }

  return testConfig.winner;
}

function firstVariant<A extends string>(testConfig: TestConfig<A>) {
  const firstVariant = testConfig['alternatives'][0];

  if (typeof firstVariant === 'object') {
    return firstVariant.name;
  } else {
    return firstVariant;
  }
}

// Should exactly match the same-named function in lib/ab_test.rb
export function deterministicVariant<A extends string>(
  id: number | string,
  testName: string,
  variants: TestConfig<A>['alternatives'],
) {
  const weightedVariants = Object.fromEntries(
    variants.map((v) => {
      if (typeof v === 'string') {
        return [v, 1];
      } else {
        return [v.name, v.percent];
      }
    }),
  );

  const tickets = Object.values(weightedVariants).reduce(
    (sum: number, weight: number) => sum + weight,
    0,
  );

  let ticket = Math.ceil(
    ((parseInt(md5(`${id}${testName}`).slice(-12), 16) % 100000) / 100000.0) *
      tickets,
  );

  const bucket = Object.entries(weightedVariants).find((variant) => {
    if (variant[1] >= ticket) {
      return true;
    } else {
      ticket -= variant[1];
      return false;
    }
  });

  const variant = bucket?.[0];

  if (isVariant(variant, variants)) {
    return variant;
  } else {
    throw new Error(`Error getting variant for ${testName}`);
  }
}

// Equivalent to in_winner_holdout? method in lib/ab_test.rb
export function __inWinnerHoldout_base(
  testName: string,
  testConfig: TestConfig<string>,
): boolean {
  if (!testConfig || !testConfig.finished) {
    captureMessage(
      `Attempted to check winner holdout for non-finished test ${testName}`,
    );
    return false;
  }

  try {
    const cookies = JSON.parse(Cookies.get('ab_tests') ?? '{}');
    const cookieTest = cookies[testName];
    if (cookieTest) {
      return cookieTest === 'winner_holdout';
    }
  } catch {
    Cookies.remove('ab_tests');
  }

  const userId = getCurrentUserId();

  if (userId === undefined) {
    captureMessage('Holdouts require a valid user_id');
    return false;
  }

  const winnerHoldoutPercent = testConfig.winner_holdout_percent || 0;
  const winnerHoldoutKey = `${testName}_winner_holdout`;

  return (
    deterministicVariant(userId, winnerHoldoutKey, [
      { name: 'winner_holdout', percent: winnerHoldoutPercent },
      { name: 'normal_holdout', percent: 100 - winnerHoldoutPercent },
    ]) === 'winner_holdout'
  );
}

// Equivalent to holdout? method in lib/ab_test.rb
// Useful for preserving holdout behavior for finished tests.
function inHoldout(
  holdoutKey: string,
  holdoutPercent: number,
  userId: number | string,
): boolean {
  const variant = deterministicVariant(userId, holdoutKey, [
    { name: 'holdout', percent: holdoutPercent },
    { name: 'non_holdout', percent: 100 - holdoutPercent },
  ]);
  return variant === 'holdout';
}

function abTestVariant<A extends string>(test: string, config: TestConfig<A>) {
  const fromParam = new URLSearchParams(decodeURI(window.location.search)).get(
    `ab_test[${test}]`,
  );
  if (fromParam && isVariant(fromParam, config.alternatives)) {
    return fromParam;
  }

  const fromOverrideCookie = getTestOverrideFromCookie(test);
  if (
    fromOverrideCookie &&
    isVariant(fromOverrideCookie, config.alternatives)
  ) {
    return fromOverrideCookie;
  }

  if (config.finished) {
    return getVariantFromFinishedTest(config);
  }

  try {
    const cookies = JSON.parse(Cookies.get('ab_tests') ?? '{}');
    const cookieTest = cookies[test];
    if (cookieTest && isVariant(cookieTest, config.alternatives)) {
      return cookieTest;
    }
  } catch {
    Cookies.remove('ab_tests');
  }

  // if active: false or unset and test is not finished,
  // return holdout or first alternative (usually control)
  if (!config.active) {
    return isHoldoutTest(config) ? ('holdout' as const) : firstVariant(config);
  }

  const uniqueId = getCurrentUserId() || window.$auvid;
  if (!uniqueId) {
    captureMessage('Need user id or auvid to bucket test');
    return firstVariant(config);
  }

  if (isHoldoutTest(config)) {
    if (getCurrentUserId() === undefined) {
      captureMessage('Holdouts require a valid user_id');
      return firstVariant(config);
    }
    const holdoutPercent = config.holdout_percent || 10;
    if (inHoldout(config.holdout, holdoutPercent, uniqueId)) {
      return 'holdout' as const;
    }
  }

  return deterministicVariant(uniqueId, test, config.alternatives);
}

/**
 * @deprecated Do not use this function directly. Instead, import from an individual test module.
 * e.g.:
 * ```typescript
 * import { abTest as myTeamsCurrentAbTest } from 'js/ab_tests/tests/my_teams_current_ab_test';
 *
 * console.log(`This user's bucket: ${myTeamsCurrentAbTest()}`);
 * ```
 */
export function __abTest_base<A extends string>(
  test: string,
  config: TestConfig<A>,
) {
  const variant = abTestVariant(test, config);
  const alreadyBucketed = window.sessionStorage.getItem(`ab_test:${test}`);

  if (alreadyBucketed == null) {
    record_ab_test(test, variant);
    window.sessionStorage.setItem(`ab_test:${test}`, variant);
  }

  return variant;
}

/**
 * @deprecated Do not use this hook directly. Instead, import from an individual test module.
 * e.g.:
 * ```typescript
 * import { abTest as myTeamsCurrentAbTest } from 'js/ab_tests/tests/my_teams_current_ab_test';
 *
 * const MyComponent = () => {
 *   const bucket = useMyTeamsCurrentAbTest();
 *   return <div>{`This user's bucket: ${bucket}`}</div>;
 * };
 * ```
 */
export function __useAbTest_base<A extends string>(
  test: string,
  config: TestConfig<A>,
) {
  const variant = abTestVariant(test, config);

  // react wants all hooks to start with use, this one is special because we
  // don't want anyone using it explicitly.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    record_ab_test(test, variant);
  }, [test, variant]);

  return variant;
}
